import { css } from "@emotion/react";
import { useEffect, useState } from "react";
import { Heading, Icon, Icons, Label, LinkButton, Text } from "@phoenix/components";
import { useNotifyError, useNotifySuccess } from "@phoenix/contexts";

import {
  GLOBAL_SCOPE_STORAGE_KEY,
  isEvaluationScope,
  readStoredScope,
  scopeLabel,
  storeScope,
  type EvaluationScope,
} from "./evaluationScope";
import { fetchApi } from "./templateApi";

const pageContainer = css`
  display: flex;
  flex-direction: column;
  gap: var(--global-dimension-size-150);
  padding: var(--global-dimension-size-150);
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const headerSection = css`
  display: flex;
  flex-direction: column;
  gap: var(--global-dimension-size-50);
  margin-bottom: var(--global-dimension-size-50);
`;

const cardsGrid = css`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: var(--global-dimension-size-150);
  width: 100%;
`;

const cardStyles = (isTrace: boolean) => css`
  display: flex;
  flex-direction: column;
  border-radius: 24px;
  background: var(--global-color-gray-75);
  padding: var(--global-dimension-size-150);
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.04);
  transition: all 250ms cubic-bezier(0.4, 0, 0.2, 1);
  box-sizing: border-box;
  min-height: 380px;

  border: 1.5px solid ${isTrace ? "rgba(59, 130, 246, 0.12)" : "rgba(16, 185, 129, 0.12)"};

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px ${isTrace ? "rgba(59, 130, 246, 0.08)" : "rgba(16, 185, 129, 0.08)"};
    border-color: ${isTrace ? "rgba(59, 130, 246, 0.3)" : "rgba(16, 185, 129, 0.3)"};
  }
`;

const cardHeader = css`
  display: flex;
  flex-direction: column;
  gap: var(--global-dimension-size-100);
  margin-bottom: var(--global-dimension-size-100);
`;

const iconBadge = (isTrace: boolean) => css`
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background: ${isTrace ? "rgba(59, 130, 246, 0.08)" : "rgba(16, 185, 129, 0.08)"};
  color: ${isTrace ? "var(--color-primary-900)" : "var(--color-success-900)"};
`;

const cardTitle = css`
  font-size: 1.45rem;
  font-weight: 700;
  color: var(--global-text-color-900);
`;

const cardDescription = css`
  font-size: 13px !important;
  line-height: 1.5 !important;
  font-weight: 400 !important;
  color: var(--global-text-color-500, #64748b) !important;
`;

const cardFooter = css`
  margin-top: auto;
  width: 100%;
`;

const selectButtonStyles = (isTrace: boolean) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.85rem 1.25rem;
  border-radius: 14px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: all 200ms ease;
  box-sizing: border-box;

  background: var(--global-color-gray-100);
  color: var(--global-text-color-900);
  border: 1px solid ${isTrace ? "rgba(59, 130, 246, 0.14)" : "rgba(16, 185, 129, 0.14)"};

  &:hover {
    background: ${isTrace ? "rgba(59, 130, 246, 0.2)" : "rgba(16, 185, 129, 0.2)"};
    color: var(--global-text-color-900);
    border-color: transparent;
  }
`;

const scopeCard = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--global-dimension-size-100);
  padding: var(--global-dimension-size-100) var(--global-dimension-size-150);
  border-radius: 20px;
  background: var(--global-color-gray-75);
  border: 1px solid rgba(15, 23, 42, 0.08);
`;

const scopeCardHeader = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  align-items: flex-start;
`;

const scopeGroup = css`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--global-dimension-size-100);
`;

const scopeOption = (isSelected: boolean) => css`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 9999px;
  border: 1px solid
    ${isSelected
      ? "var(--ac-global-color-primary, #3b82f6)"
      : "rgba(15, 23, 42, 0.14)"};
  background: ${isSelected ? "rgba(59, 130, 246, 0.06)" : "transparent"};
  color: inherit;
  font-size: 0.875rem;
  font-weight: ${isSelected ? 600 : 400};
  white-space: nowrap;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
`;

const scopeBullet = (isSelected: boolean) => css`
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  box-sizing: border-box;
  border: 1px solid
    ${isSelected
      ? "var(--ac-global-color-primary, #3b82f6)"
      : "rgba(15, 23, 42, 0.35)"};
  background: ${isSelected
    ? "var(--ac-global-color-primary, #3b82f6)"
    : "transparent"};
`;

export function TemplatesPage() {
  const notifySuccess = useNotifySuccess();
  const notifyError = useNotifyError();
  const [scope, setScope] = useState<EvaluationScope>(() => readStoredScope());
  const [saving, setSaving] = useState(false);

  // Seed from the server-side global default (defaults.scope) unless this
  // browser already holds an explicit choice.
  useEffect(() => {
    let active = true;
    async function fetchScopeDefault() {
      try {
        const response = await fetchApi("/v1/templates/settings");
        if (!response.ok) {
          return;
        }
        const data = await response.json().catch(() => null);
        const stored = localStorage.getItem(GLOBAL_SCOPE_STORAGE_KEY);
        if (active && isEvaluationScope(data?.scope) && !isEvaluationScope(stored)) {
          setScope(data.scope);
        }
      } catch {
        // The default is supplementary; the built-in trace default applies.
      }
    }
    fetchScopeDefault();
    return () => {
      active = false;
    };
  }, []);

  const changeScope = async (next: EvaluationScope) => {
    if (next === scope || saving) {
      return;
    }
    const previous = scope;
    setScope(next);
    storeScope(next);
    setSaving(true);
    try {
      const response = await fetchApi("/v1/templates/scope", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scope: next }),
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(body?.detail || response.statusText);
      }
      const updated = typeof body?.updated === "number" ? body.updated : 0;
      notifySuccess({
        title: "Evaluation scope updated",
        message:
          updated > 0
            ? `${scopeLabel(next)} now applies to all evaluations; ${updated} running monitor(s) switched.`
            : `${scopeLabel(next)} now applies to all evaluations.`,
      });
    } catch (error) {
      setScope(previous);
      storeScope(previous);
      notifyError({
        title: "Failed to update evaluation scope",
        message: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div css={pageContainer}>
      <div css={headerSection}>
        <Heading level={1}>Evaluation Templates</Heading>
        <Text color="text-500" size="M" css={css`margin-top: var(--global-dimension-size-25);`}>
          Choose the type of evaluation template you want to work with.
        </Text>
      </div>

      <div css={scopeCard}>
        <div css={scopeCardHeader}>
          <Label>Evaluation scope</Label>
          <Text color="text-500" size="S">
            Applies to every live evaluation, including running monitors.
          </Text>
        </div>
        <div role="radiogroup" aria-label="Evaluation scope" css={scopeGroup}>
          {(["trace", "observation"] as EvaluationScope[]).map((option) => {
            const isSelected = scope === option;
            return (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={isSelected}
                disabled={saving}
                onClick={() => void changeScope(option)}
                css={scopeOption(isSelected)}
              >
                <span aria-hidden="true" css={scopeBullet(isSelected)} />
                <span>{scopeLabel(option)}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div css={cardsGrid}>
        {/* ── Trace Evaluation Card ── */}
        <div css={cardStyles(true)}>
          <div css={cardHeader}>
            <div css={iconBadge(true)}>
              <Icon svg={<Icons.GitBranchOutline />} />
            </div>
            <div>
              <Heading level={2} css={cardTitle}>Trace Evaluation Templates</Heading>
              <Text css={cardDescription}>
                Evaluate agent traces, workflows, tool usage, reasoning and overall task execution.
              </Text>
            </div>
          </div>

          <div css={cardFooter}>
            <LinkButton 
              to="/templates/trace" 
              css={selectButtonStyles(true)}
            >
              <span>View Trace Templates</span>
              <Icon svg={<Icons.ArrowRight />} />
            </LinkButton>
          </div>
        </div>

        {/* ── Dataset Evaluation Card ── */}
        <div css={cardStyles(false)}>
          <div css={cardHeader}>
            <div css={iconBadge(false)}>
              <Icon svg={<Icons.DatabaseOutline />} />
            </div>
            <div>
              <Heading level={2} css={cardTitle}>Dataset Evaluation Templates</Heading>
              <Text css={cardDescription}>
                Evaluate model outputs against reference answers or ground truth and measure response quality.
              </Text>
            </div>
          </div>

          <div css={cardFooter}>
            <LinkButton 
              to="/templates/dataset" 
              css={selectButtonStyles(false)}
            >
              <span>View Dataset Templates</span>
              <Icon svg={<Icons.ArrowRight />} />
            </LinkButton>
          </div>
        </div>
      </div>
    </div>
  );
}
